/** This class manages video streams on canvas */
class CanvasStreamController {
  private static instance: CanvasStreamController
  private activeStreams: {
    videoElement: HTMLVideoElement
    canvasElement: HTMLCanvasElement
    wrapperElement: HTMLDivElement
  }[] = []
  private animationFrameId: number | null = null

  private constructor() {
    // Ensure only one instance exists
    if (!CanvasStreamController.instance) {
      CanvasStreamController.instance = this
    }

    return CanvasStreamController.instance
  }

  /** Returns the singleton instance of CanvasStreamController */
  public static getInstance(): CanvasStreamController {
    return CanvasStreamController.instance || new CanvasStreamController()
  }

  /** Resets the controller and stops all streaming */
  public reset(): void {
    this.activeStreams = []
    cancelAnimationFrame(this.animationFrameId!)
    this.animationFrameId = null
  }

  /** Adds a video stream to be rendered on a canvas. */
  public drawVideo(
    videoElement: HTMLVideoElement,
    canvasElement: HTMLCanvasElement,
    wrapperElement: HTMLDivElement
  ): void {
    // Add the video and canvas elements to active streams
    this.activeStreams.push({ videoElement, canvasElement, wrapperElement })

    // Start drawing frames if not already active
    if (this.activeStreams.length === 1) {
      this.drawVideoFrames()
    }
  }

  /** Removes a video stream from the controller. */
  public removeVideo(
    videoElement: HTMLVideoElement,
    canvasElement: HTMLCanvasElement
  ): void {
    const index = this.activeStreams.findIndex(
      item =>
        item.videoElement === videoElement &&
        item.canvasElement === canvasElement
    )

    const indexFound = index !== -1
    if (indexFound) {
      // Remove the video stream from active streams
      this.activeStreams.splice(index, 1)
    }

    // Stop drawing frames if no active streams
    const noActiveStreamPresent = this.activeStreams.length === 0
    if (noActiveStreamPresent) {
      cancelAnimationFrame(this.animationFrameId!)
      this.animationFrameId = null
    }
  }

  /** Get proper image position and dimension for rendering on canvas */
  private getCanvasImageDimension({
    canvasWidth,
    canvasHeight,
    imageWidth,
    imageHeight
  }) {
    const canvasAspectRatio = canvasWidth / canvasHeight
    const imgAspectRatio = imageWidth / imageHeight

    let x = 0
    let y = 0
    let width = canvasWidth
    let height = canvasHeight

    if (imgAspectRatio > canvasAspectRatio) {
      // Image is wider than the canvas
      width = imgAspectRatio * canvasHeight
      x = (canvasWidth - width) / 2
    } else {
      // Image is taller than or equal to the canvas
      height = canvasWidth / imgAspectRatio
      y = (canvasHeight - height) / 2
    }

    return { x, y, width, height }
  }

  private drawVideoFrames(): void {
    // Traverse all the video element and render the video on canvas
    this.activeStreams.forEach(
      ({ videoElement, canvasElement, wrapperElement }) => {
        const context = canvasElement.getContext("2d")
        const imageWidth = videoElement.videoWidth
        const imageHeight = videoElement.videoHeight
      
        // Calculate scale value, by finding minimum scale value which doesn't impact video quality.
        const scale = Math.ceil(
          Math.max(
            imageWidth / wrapperElement.clientWidth,
            imageHeight / wrapperElement.clientHeight
          )
        )

        // Scale the canvas size to improve the video quality
        const canvasWidth = wrapperElement.clientWidth * scale
        const canvasHeight = wrapperElement.clientHeight * scale      

        // set canvas width and height
        canvasElement.width = canvasWidth
        canvasElement.height = canvasHeight

        const { x, y, width, height } = this.getCanvasImageDimension({
          canvasHeight,
          canvasWidth,
          imageHeight,
          imageWidth
        })

        // Render video frame on the canvas
        context.drawImage(videoElement, x, y, width, height)
      }
    )

    // Request the next animation frame
    this.animationFrameId = requestAnimationFrame(
      this.drawVideoFrames.bind(this)
    )
  }
}

// Singleton instance
const canvasStreamController = CanvasStreamController.getInstance()

export default canvasStreamController
